import { ChatKnowledgeService } from '@agent/agent-interface/contact-view/chat/services/chat-knowledge.service';
import { ISocialNetworkFrameService } from '@agent/agent-interface/contact-view/social-network/services/social-network-frame.service';
import { IEpticaDeprecatedService } from '@agent/core/channel-digit/mail/eptica/eptica-deprecated.service';
import { EpticaHelper } from '@agent/core/channel-digit/mail/eptica/eptica.helper';
import { IEpticaFrame, IEpticaRequestFrame } from '@agent/core/channel-digit/mail/eptica/models/eptica.request.interfaces';
import { KnowledgeBaseFacade } from '@agent/core/store/knowledge-base/knowledge-base.facade';
import { Inject, Injectable } from '@angular/core';
import { NG1_CHAT_KNOWLEDGE, NG1_EPTICA, NG1_SOCIAL_NETWORK_FRAME_SERVICE } from '@commons/core/core.provider.ajs';
import { AppExternalService } from '@commons/core/ui/app-external.service';
import { Observable, take } from 'rxjs';

import { Booking, BookingFacade, ChannelType, HttpService } from '@contact/upgrade';

import { ResponseModel, ResponseModelAttachment } from '../../models/responseModel';
import { Domain, GroupCodeTypeEnumeration } from '../models/domain';
import { LinkTypeEnumeration, OwiMdr, OwiResponse } from '../models/responseModel';
import { KnowledgeBaseOwiApiService } from './knowledge-base-OWI-api.service';
import { identity } from 'angular';

@Injectable({
  providedIn: 'root',
})
export class KnowledgeBaseOwiService {
  booking: Booking;

  constructor(
    private readonly contactFacade: BookingFacade,
    private readonly http: HttpService,
    private readonly appExternalService: AppExternalService,
    private readonly knowledgeBaseOwiApiService: KnowledgeBaseOwiApiService,
    private readonly knowledgeBaseFacade: KnowledgeBaseFacade,
    @Inject(NG1_SOCIAL_NETWORK_FRAME_SERVICE) private readonly socialNetworkservice: ISocialNetworkFrameService,
    @Inject(NG1_CHAT_KNOWLEDGE) private readonly chatKnowledgeService: ChatKnowledgeService,
    @Inject(NG1_EPTICA) private readonly epticaService: IEpticaDeprecatedService
  ) {
    this.contactFacade.selected$.subscribe((contact: Booking) => (this.booking = contact));
  }

  public getMediaExternalId(): string {
    let mediaExternalId = '';
    if (this.booking) {
      switch (this.booking.channel.type) {
        case ChannelType.SOCIAL_NETWORK:
        case ChannelType.CHAT:
          mediaExternalId = this.booking.responseId;
          break;
        case ChannelType.MAIL:
        case ChannelType.UNITARY_MAIL:
          mediaExternalId = this.booking.epticaRequestId ? this.booking.epticaRequestId.toString() : '';
          break;
        default:
          mediaExternalId = '';
      }
    }
    return mediaExternalId;
  }

  public checkSuggestionsForMedia() {
    const mediaExternalId = this.getMediaExternalId();
    if (mediaExternalId)
      this.knowledgeBaseOwiApiService.getSuggestedResults(mediaExternalId).subscribe((response: OwiResponse) => {
        const suggestedResults = response.listeMdr.map((responseModel: OwiMdr) => ({
          id: responseModel.mdrNum,
          label: responseModel.libelle,
          content: responseModel.contenu,
          linkType: responseModel.typeLien,
          link: responseModel.lien,
          linkText: responseModel.texteLien,
          attachments: responseModel.attachments.map(owiAttachment => ({
            responseModelId: owiAttachment.mdrNum,
            name: owiAttachment.name,
            content: owiAttachment.content,
          })),
        }));

        this.knowledgeBaseFacade.suggestedResults(suggestedResults);
      });
  }

  public sendKoResponse(mediaExternalId: string): Observable<Object> {
    return this.knowledgeBaseOwiApiService.sendKoNotification(mediaExternalId, 'ko_reponse');
  }

  public sendKoSkills(mediaExternalId: string): Observable<Object> {
    return this.knowledgeBaseOwiApiService.sendKoNotification(mediaExternalId, 'ko_tri');
  }

  public sendKoDraft(mediaExternalId: string): Observable<Object> {
    return this.knowledgeBaseOwiApiService.sendKoNotification(mediaExternalId, 'effacer_brouillon');
  }

  public isResponseModel(domain: Domain): boolean {
    return domain.codeType === GroupCodeTypeEnumeration.BUSINESS;
  }

  public recursiveDomainBuild(domain: Domain): Domain {
    let children: Domain[] = [];
    if (!domain.leaf) {
      domain.children.forEach((child: Domain) => {
        children.push(this.recursiveDomainBuild(child));
      });
    }

    return {
      id: domain.id,
      num: domain.num,
      type: domain.type,
      numNo: domain.numNo,
      ddoNum: domain.ddoNum,
      ddoDdoNum: domain.ddoDdoNum,
      libelle: domain.libelle,
      codeType: domain.codeType,
      leaf: domain.leaf,
      children,
    };
  }

  public copyPaste(responseModel: ResponseModel): void {
    if (this.booking) {
      const mediaExternalId = this.getMediaExternalId();
      switch (this.booking.channel.type) {
        case ChannelType.SOCIAL_NETWORK:
          if (responseModel) {
            this.socialNetworkservice.sendPostMessageToFrame(mediaExternalId, responseModel.content);
          }
          break;
        case ChannelType.CHAT:
          if (responseModel) {
            this.http.get(responseModel.link).subscribe(response => {
              let file = new File([new Blob([response as any], {})], responseModel.link);
              this.chatKnowledgeService.setKnowledgeBaseMessage(mediaExternalId, responseModel.content, [file]);
            });
          }
          break;
        case ChannelType.MAIL:
        case ChannelType.UNITARY_MAIL:
          let request$: Observable<IEpticaFrame>;

          if (mediaExternalId) {
            request$ = this.epticaService.loadRequest$({
              requestId: Number(mediaExternalId) || 0,
            });
          } else {
            request$ = this.epticaService.loadOutgoingRequest$({
              idType: 'ias',
              customerId: this.booking.customerId || '',
              responseId: this.booking.responseId,
            });
          }
          request$.pipe(take(1)).subscribe((request: IEpticaRequestFrame) => this.insertResponseModel(request, responseModel));
          break;
      }
    }
  }

  private insertResponseModel(request: IEpticaFrame, responseModel: ResponseModel) {
    this.appExternalService.getApplicationContainer(request.getFrameId()).forEach(frame => {
      EpticaHelper.addHtmlEditorText(frame.name, responseModel.content);
      if (!!responseModel && !!responseModel.link && responseModel.linkType == LinkTypeEnumeration.DOWNLOAD) {
        this.downloadFile(responseModel.link, fileObject => {
          fileObject.name = responseModel.linkText || responseModel.link.split('/').pop();

          let file = fileObject;
          // upload file into Eptica's dropzone
          if (!!file) {
            request!.addUploadFiles([file]);
          }
        });
      }

      if (!!responseModel && responseModel.attachments.length) {
        responseModel.attachments.map(attachment =>
          this.downloadFileByResponseModelIdAndFileName(attachment.responseModelId, attachment.name, fileObject => {
            fileObject.name = attachment.name;

            let file = fileObject;
            // upload file into Eptica's dropzone
            if (!!file) {
              request!.addUploadFiles([file]);
            }
          })
        );
      }
    });
  }

  public getFileUrlByPath(path: string): string {
    return this.knowledgeBaseOwiApiService.getFileUrlByPath(path);
  }

  public getFileUrlByResponseModelIdAndFileName(responseModelId: string, name: string): string {
    return this.knowledgeBaseOwiApiService.getFileUrlByResponseModelIdAndFileName(responseModelId, name);
  }

  private downloadFile(url: string, cb: Function = () => {}): void {
    this.knowledgeBaseOwiApiService.downloadFile(url).subscribe(blob => cb(blob));
  }

  public downloadFileByResponseModelIdAndFileName(responseModelId: string, name: string, cb: Function = () => {}): void {
    this.knowledgeBaseOwiApiService.downloadFileByResponseModelIdAndFileName(responseModelId, name).subscribe(blob => cb(blob));
  }
}
